import { InquiryTemplateThemeFormData } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTheme';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  ExpirationIntervals,
  InquiryTemplateVersionStep,
} from 'dashboard/models/inquiry-template-version';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import { ThemeAttributes } from 'dashboard/models/theme';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { WorkflowStep, WorkflowVersion } from 'dashboard/models/workflow-version';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import type { StepPreview } from 'lib/interfaces/StepsConfig';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import { ImageKey } from 'lib/interfaces/ui-components';
import { DeepPartial } from 'lib/types';
import { RedirectQueryStringConfig } from 'verify/models/inquiry';

export type InquiryTemplateVersionFormData = Partial<
  InquiryTemplateEditorFormData & InquiryTemplateThemeFormData
>;

export type ITEFormDataKey = keyof InquiryTemplateEditorFormData;
export type FormFieldDirtyMap = Record<ITEFormDataKey, boolean>;
export interface InquiryTemplateEditorFormData {
  'template-name': string;
  'template-description': string;
  'allowed-hosts': string[];
  'disable-client-create': boolean;
  'disable-device-handoff-inputs-if-prefilled': boolean;
  'enable-device-handback': boolean;
  'enabled-locales': InquiryIso6391LanguageCode[];
  'expiration-intervals': ExpirationIntervals;
  'max-session-limit': number | null;
  'field-key-device-handoff-email': string;
  'field-key-device-handoff-phone-number': string;
  'field-schema': FieldSchema[];
  'has-reusable-persona-config': boolean;
  'redirect-query-string-config': RedirectQueryStringConfig;
  'redirect-uri': string;
  description?: string;
  matchRequirementProfiles: MatchRequirementProfileMap;
  steps: InquiryTemplateVersionStep[];
  theme?: ThemeAttributes;
  themeSetId?: string;
  verificationTemplateVersions: VerificationTemplateVersionMap;
  workflowVersions: WorkflowVersionMap;
}

export type InquiryTemplateUpdateData = {
  name?: string;
  description?: string;
};

export type InquiryTemplateVersionUpdateData = Partial<
  Pick<
    InquiryTemplateEditorFormData,
    | 'allowed-hosts'
    | 'description'
    | 'disable-client-create'
    | 'disable-device-handoff-inputs-if-prefilled'
    | 'enable-device-handback'
    | 'enabled-locales'
    | 'expiration-intervals'
    | 'field-key-device-handoff-email'
    | 'field-key-device-handoff-phone-number'
    | 'field-schema'
    | 'redirect-query-string-config'
    | 'redirect-uri'
    | 'steps'
    | 'theme'
    | 'themeSetId'
  >
> & {
  'asset-data'?: string;
  'asset-filename'?: string;
  'restored-from'?: string | undefined;
};

export type AssetUploadData = {
  'asset-data': string;
  'asset-filename': string;
};

export enum AssetType {
  UPLOADED = 'uploaded_asset',
  PERSONA = 'persona_asset',
}

export type Asset = UploadedAsset | PersonaAsset;

export interface UploadedAsset {
  type: AssetType.UPLOADED;
  url: string;
  'blob-id': string;
  filename: string;
  'content-type': string;
  'aspect-ratio'?: number;
}

type PrivatePersonaAssetImageKey =
  | ImageKey.CreatePersonaPasskeyRegistrationHero
  | ImageKey.CreatePersonaPasskeyRegistrationSpeedIcon
  | ImageKey.CreatePersonaPasskeyRegistrationShieldIcon
  | ImageKey.CreatePersonaPasskeyRegistrationFingerprintIcon
  | ImageKey.CreatePersonaSuccessHero
  | ImageKey.GovernmentIdNfcScanBackClosed
  | ImageKey.GovernmentIdNfcScanFrontClosed
  | ImageKey.GovernmentIdNfcScanPhotoPage
  | ImageKey.GovernmentIdNfcScanIdCard
  | ImageKey.GovernmentIdNfcScanBackThenFront;

export interface PersonaAsset {
  type: AssetType.PERSONA;
  imageKey: Exclude<ImageKey, PrivatePersonaAssetImageKey>;
}

export type LoadValidationErrorsFormData = InquiryTemplateVersionUpdateData & {
  verificationTemplateVersions: (
    | VerificationTemplateVersion
    | DeepPartial<VerificationTemplateVersion>
  )[];
  workflowVersions: WorkflowVersion[];
};

export type HandleSaveInquiryTemplateVersionProps = {
  formValues: InquiryTemplateVersionFormData;
  inquiryTemplate: InquiryTemplate;
  lockVersion: number;
  reloadPath: string;
};

export type StepPreviewMap = Record<string, StepPreview>;
export type PreviewPageMap = Record<string, React.ReactElement[]>;
export type VerificationTemplateVersionMap = Record<
  string,
  VerificationTemplateVersion | DeepPartial<VerificationTemplateVersion>
>;
export type WorkflowVersionMap = Record<string, WorkflowVersion>;
export type MatchRequirementProfileMap = Record<string, MatchRequirementProfile[]>;
export type ScrollRefMap = Record<string, React.MutableRefObject<HTMLDivElement>>;

export type CombinedStepType =
  | UiStepType.GovernmentId
  | UiStepType.Selfie
  | UiStepType.SelfieReflection
  | UiStepType.Document;

export type TemplateStepPreview = InquiryTemplateVersionStep & {
  token: string;
  stepPreview?: StepPreview;
  page?: React.ReactElement;
  combinedStepPages?: React.ReactElement[];
};

export interface LinkedWorkflowStep {
  type: 'workflow';
  token: string;
  workflowVersionId: string;
  nextSteps: string[]; // token of wfv step or name of template step
  // include only minimum necessary attributes for rendering the tree
  // in order to prevent unnecessary re-renders for inline workflow step edits (routes, conditionals, etc.)
  workflowStep: Pick<WorkflowStep, 'type' | 'token' | 'name'> & {
    isUpdateNextStepAction?: boolean;
  };
}

export type TemplateRunStepPreview = TemplateStepPreview & {
  isInRunPath?: boolean;
  chosenIndexes?: number[];
};

export type LinkedWorkflowRunStep = LinkedWorkflowStep & {
  isInRunPath?: boolean;
  chosenIndexes?: number[];
};

export type CombinedStep = TemplateStepPreview | LinkedWorkflowStep;
export type CombinedRunStep = TemplateRunStepPreview | LinkedWorkflowRunStep;

/**
 * A representation of a CombinedStep that allows backtracking.
 */
export interface TreeNode {
  parent: string | undefined;
  /**
   * A special case of `parent`. Any parent besides the first is assumed to
   * come from a retry. Retries form cycles in the graph.
   */
  retryParents: string[];
  step: CombinedStep;
  children: string[];
}

export type CombinedStepForTree = CombinedStep & {
  workflowStepType?: WorkflowStep['type'];
  branches?: CombinedStepForTree[][];
  isConnector?: boolean;
};

export type CombinedRunStepForTree = CombinedRunStep & {
  workflowStepType?: WorkflowStep['type'];
  branches?: CombinedStepForTree[][];
  isConnector?: boolean;
  isInRunPath?: boolean;
  chosenIndexes?: number[];
};

export type ScreenDimensions = {
  width: number;
  height: number;
};

/* a Mapping of inquiry template version id to screen dimensions */
export type ScreenDimensionMap = Record<string, ScreenDimensions>;
